import axios from 'axios';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as adminApi from '@/api/admin';
import type { AdminDesignQuery } from '@/api/admin';

// ═══════════════════════════════════════════════════════════════════
// QUERY KEYS
// ═══════════════════════════════════════════════════════════════════

export const adminDesignKeys = {
  all: ['admin', 'designs'] as const,
  lists: () => [...adminDesignKeys.all, 'list'] as const,
  list: (query?: AdminDesignQuery) =>
    [...adminDesignKeys.lists(), query] as const,
  details: () => [...adminDesignKeys.all, 'detail'] as const,
  detail: (id: string) => [...adminDesignKeys.details(), id] as const,
};

// ═══════════════════════════════════════════════════════════════════
// QUERIES
// ═══════════════════════════════════════════════════════════════════

/**
 * Hook to fetch designs across all accounts (paginated, filtered).
 * Keeps the previous page on screen while the next one loads.
 */
export function useAdminDesigns(query?: AdminDesignQuery) {
  return useQuery({
    queryKey: adminDesignKeys.list(query),
    queryFn: () => adminApi.getAdminDesigns(query),
    placeholderData: (previous) => previous,
  });
}

/**
 * Hook to fetch one design of any account for the admin review
 */
export function useAdminDesign(id: string | undefined) {
  const queryClient = useQueryClient();
  const defaultRetry = queryClient.getDefaultOptions().queries?.retry;

  return useQuery({
    queryKey: adminDesignKeys.detail(id ?? ''),
    queryFn: () => adminApi.getAdminDesign(id ?? ''),
    enabled: !!id,
    // An unknown or deleted design stays unknown: skip retries on 404 and
    // keep the client's retry policy for every other failure.
    retry: (failureCount, error) => {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return false;
      }
      if (typeof defaultRetry === 'function') {
        return defaultRetry(failureCount, error);
      }
      if (typeof defaultRetry === 'boolean') return defaultRetry;
      return failureCount < (defaultRetry ?? 3);
    },
  });
}
